import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { PiX } from 'react-icons/pi'
import { ContactCompany } from 'models/companies'
import { EmailMessage, EmailThread } from 'models/emailIntegration'
import { Modal } from 'components/UI/Modal'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { EmailsModalHeader, ModalView } from './EmailsModalHeader'
import { EmailInboxView } from './EmailInboxView'
import { EmailDetailView, EmailOnlyDetailView } from './EmailDetailView'
import { ComposePanel, ComposeMode, SendPayload } from './ComposePanel'
import { InboxTab } from './InboxTabs'
import { PAGE_SIZE } from './EmailInboxView'
import { handleEmailError } from './handleEmailError'
import { LegacyEmailsModal } from './LegacyEmailsModal'
import type { LockedFilters } from './InboxFilterChips'

interface BaseEmailsModalProps {
  show: boolean
  onHide: () => void
  forDoorProxyId?: number
  companyName?: string
  /** Filters that are always applied and cannot be removed by the user. */
  lockedFilters?: LockedFilters
}

interface EmailsListModalProps extends BaseEmailsModalProps {
  /** When true, shows only the email view without list navigation */
  emailOnlyMode?: false
  contactCompany: ContactCompany
  /** If provided, opens directly to this email */
  initialEmail?: EmailMessage
  /**
   * Deep-link target. The modal opens on the list view, fetches this
   * thread, then auto-jumps to the email view once it resolves. On error
   * the modal silently falls back to the list view; the URL param stays
   * so a refresh retries.
   */
  initialThreadId?: number
}

interface EmailOnlyModalProps extends BaseEmailsModalProps {
  /** When true, shows only the email view without list navigation */
  emailOnlyMode: true
  contactCompany?: never
  /** The email to display (required in emailOnlyMode) */
  initialEmail: EmailMessage
}

type EmailsModalProps = EmailsListModalProps | EmailOnlyModalProps

export function EmailsModal(props: EmailsModalProps) {
  const featureFlag = useFeatureFlagService()

  if (!featureFlag.enableEmailInboxView) {
    return <LegacyEmailsModal {...props} />
  }

  return <InboxEmailsModal {...props} />
}

/**
 * Email modal supporting three modes: thread-based inbox view with filtering/pagination,
 * email detail view, or a compose view. Handles thread selection, navigation,
 * and state reset on close.
 */
function InboxEmailsModal(props: EmailsModalProps) {
  const {
    show,
    onHide,
    forDoorProxyId,
    companyName,
    initialEmail,
    emailOnlyMode,
    lockedFilters,
  } = props

  const contactCompany = emailOnlyMode ? undefined : props.contactCompany
  const initialThreadId = emailOnlyMode ? undefined : props.initialThreadId

  const api = useApiService()

  // Deep-link target. Fetched lazily; resolves to undefined on error so the
  // modal silently falls back to the list view (URL param stays so a refresh
  // retries).
  const { data: deepLinkedThread } = useQuery({
    queryKey: ['email-thread', initialThreadId],
    queryFn: ({ signal }) =>
      api.emailIntegration.getEmailThreadById(initialThreadId!, signal),
    enabled: !!initialThreadId && show,
    retry: false,
  })

  // `userView` / `userPickedThread` are explicit user navigation choices.
  // `null` / `undefined` mean "use the derived default" — which lets a
  // deep-linked thread auto-jump the modal into the email view once it
  // loads, without an effect. Once the user clicks anywhere in the modal,
  // the override stops applying so "back to list" sticks.
  const [userView, setUserView] = useState<ModalView | null>(null)
  const [userPickedThread, setUserPickedThread] = useState<
    EmailThread | undefined
  >()

  const view: ModalView =
    userView ??
    (emailOnlyMode || initialEmail || deepLinkedThread ? 'email' : 'list')
  const selectedThread =
    userPickedThread ?? (userView === null ? deepLinkedThread : undefined)

  const [selectedTab, setSelectedTab] = useState<InboxTab>('all_team_emails')
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | undefined>(
    initialEmail
  )

  // Keep track of the current threads list for prev/next navigation
  const threadsRef = useRef<EmailThread[]>([])
  const [threadIndex, setThreadIndex] = useState(0)
  const [listPage, setListPage] = useState(1)
  const [listTotal, setListTotal] = useState(0)
  const [initialComposeMode, setInitialComposeMode] = useState<
    ComposeMode | undefined
  >()

  // Handle initial email navigation
  useEffect(() => {
    if (show && initialEmail) {
      setSelectedEmail(initialEmail)
      setUserView('email')
    }
  }, [show, initialEmail])

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setUserView(null)
      setSelectedEmail(emailOnlyMode ? initialEmail : undefined)
      setUserPickedThread(undefined)
      setSelectedTab('all_team_emails')
      setThreadIndex(0)
    }
  }, [show, emailOnlyMode, initialEmail])

  const handleThreadClick = useCallback(
    (
      thread: EmailThread,
      index: number,
      threads: EmailThread[],
      page: number,
      total: number,
      composeMode?: ComposeMode
    ) => {
      setUserPickedThread(thread)
      setThreadIndex(index)
      threadsRef.current = threads
      setListPage(page)
      setListTotal(total)
      setInitialComposeMode(composeMode)
      setUserView('email')
    },
    []
  )

  const handleNavigateThread = useCallback(
    (delta: number) => {
      const newIndex = threadIndex + delta
      const threads = threadsRef.current
      if (newIndex >= 0 && newIndex < threads.length) {
        setUserPickedThread(threads[newIndex])
        setThreadIndex(newIndex)
      }
    },
    [threadIndex]
  )

  const handleBack = useCallback(() => {
    setUserView('list')
    setUserPickedThread(undefined)
    setSelectedEmail(undefined)
    setInitialComposeMode(undefined)
  }, [])

  const handleCompose = useCallback(() => {
    setUserView('compose')
  }, [])

  // Standalone compose send (no thread context for optimistic UI)
  const composeMutation = useMutation({
    mutationFn: ({ formData, fromId }: SendPayload) =>
      api.emailIntegration.sendEmailMessage(fromId, formData),
    onSuccess: () => toast.success('Email sent successfully'),
    onError: async (err) => handleEmailError(err, api.emailIntegration),
  })

  const handleComposeSend = useCallback(
    (payload: SendPayload) => {
      setUserView('list')
      composeMutation.mutate(payload)
    },
    [composeMutation]
  )

  const handleComposeDiscard = useCallback(() => {
    setUserView('list')
  }, [])

  const displayName = companyName || contactCompany?.name
  const pageStart = (listPage - 1) * PAGE_SIZE + 1

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      enforceFocus={false}
      backdrop="static"
      className="[&_.modal-content]:bg-surface-page [&_.modal-content]:rounded-2xl"
    >
      <button
        type="button"
        className="absolute top-5 right-[30px] z-10 flex items-center justify-center size-8 rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500"
        aria-label="Close"
        onClick={onHide}
      >
        <PiX className="size-5" />
      </button>
      <EmailsModalHeader
        view={view}
        companyName={displayName}
        emailSubject={selectedEmail?.subject}
        emailOnlyMode={emailOnlyMode}
        isDealInbox={!!lockedFilters?.deal_ids?.length}
      />

      <Modal.Body className="email-modal-scroll px-[24px] pb-[24px] pt-0">
        <div
          style={{ height: '70vh', display: 'flex', flexDirection: 'column' }}
        >
          {view === 'compose' ? (
            <ComposeEmailView
              companyName={displayName}
              onSend={handleComposeSend}
              onDiscard={handleComposeDiscard}
            />
          ) : view === 'list' && contactCompany ? (
            <EmailInboxView
              contactCompanyId={contactCompany.id}
              forDoorProxyId={forDoorProxyId}
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              onThreadClick={handleThreadClick}
              onCompose={handleCompose}
              lockedFilters={lockedFilters}
            />
          ) : emailOnlyMode && selectedEmail ? (
            <EmailOnlyDetailView email={selectedEmail} />
          ) : selectedThread ? (
            <EmailDetailView
              thread={selectedThread}
              threadIndex={threadIndex}
              totalThreads={listTotal}
              pageSize={PAGE_SIZE}
              pageStart={pageStart}
              contactCompanyId={contactCompany!.id}
              contactCompany={contactCompany}
              companyName={displayName}
              initialComposeMode={initialComposeMode}
              onBack={handleBack}
              onNavigateThread={handleNavigateThread}
            />
          ) : selectedEmail ? (
            <EmailOnlyDetailView email={selectedEmail} />
          ) : null}
        </div>
      </Modal.Body>
    </Modal>
  )
}

// ─── Standalone compose view ─────────────────────────────────────────────────

interface ComposeEmailViewProps {
  companyName?: string
  onSend: (payload: SendPayload) => void
  onDiscard: () => void
}

/** Full-height compose view. Cancel (in the compose toolbar) returns to the
 *  inbox list, so no separate back-button row is needed. */
function ComposeEmailView({
  companyName,
  onSend,
  onDiscard,
}: ComposeEmailViewProps) {
  return (
    <div className="h-full flex flex-col">
      <ComposePanel
        mode="compose"
        onSend={onSend}
        onDiscard={onDiscard}
        companyName={companyName}
        standalone
      />
    </div>
  )
}
